import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, fetchCompanyOptions, fetchCompetentPersonOptions } from '../lib/api.js';
import type { PaginatedResponse, Form11Certificate } from '@gng-ehs/shared/types';
import { checkLicenceStatus } from '@gng-ehs/shared';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { useDebounce } from '../hooks/useDebounce.js';
import { AsyncSearchableSelect } from '../components/AsyncSearchableSelect.js';
import { Pagination } from '../components/Pagination';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';
import { TableLoader } from '../components/TableLoader';
import { CompanyHoverBadge } from '../components/CompanyHoverBadge';
import { ShortCodeTag } from '../components/ShortCodeTag';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly } from '../lib/dateUtils';
import { InlinePersonnelForm } from '../components/InlinePersonnelForm.js';
import { InlineCompanyForm } from '../components/InlineCompanyForm.js';
import { generateForm11ClientPDF } from '../lib/pdf-form11-client.js';
import { Form11ExcelImportModal } from '../components/Form11ExcelImportModal.js';
import { Form11ExportIndexModal } from '../components/Form11ExportIndexModal.js';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/BulkActionBar';

interface Form11 extends Form11Certificate {
  company_name?: string;
  company_short_code?: string;
  inspector_name?: string;
}

interface Company {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name?: string;
  factory_address?: string;
  license_number?: string;
  registration_number?: string;
  nic_code?: string;
  contact_email?: string;
  communication_email?: string;
}

interface Personnel {
  id: string;
  full_name: string;
  designation?: string;
  signature_url?: string;
  stamp_url?: string;
  competency_cert_number?: string;
  competency_valid_from?: string;
  competency_valid_upto?: string;
  issuing_authority?: string;
}

const INITIAL_FORM11_VALUES = {
  company_id: '',
  personnel_id: '',
  examination_date: new Date().toISOString().split('T')[0],

  vessel_type_select: 'Vertical Air Receiver',
  vessel_name_description: 'Vertical Air Receiver',
  vessel_serial_no: 'VART/01',
  vessel_capacity: '1 KL',
  vessel_model_no: '',
  vessel_location: 'Near Compressor Room',
  vessel_description: '',

  manufacturer_name_address: '',
  nature_of_process: 'For storage and supply of air at 6 Kg/cm²',

  date_of_construction: '1992',
  thickness_shell: '5.5',
  thickness_dish: '8',
  thickness_top_dish: '',
  thickness_bottom_dish: '',
  first_taken_into_use_date: '1992',
  safe_working_pressure_recommended: '6 Kg/cm²',

  last_external_exam_date: '2025-06-13',
  last_internal_exam_date: 'Inaccessible',
  last_hydraulic_exam_details: 'Last hydro test done 9 Kg/Cm2 on 11/04/2023 by Jagdish Patel',
  last_ultrasonic_exam_date: '2025-06-13',

  lagging_removed_status: 'Non-Insulated Vessel',

  exam_external_findings: '',
  exam_internal_findings: '(b) Inaccessible',
  exam_hydraulic_findings: '',
  hydro_test_pressure: '9 Kg/cm²',
  exam_ultrasonic_findings: 'Shell: - 5, 5.1, 5 mm, Dish: - 7.7, 7.65 mm',

  condition_vessel: 'Good',
  condition_piping: 'Good',

  fittings_pressure_gauges: 'Good',
  fittings_safety_valve: 'Good',
  fittings_stop_valve: 'Good',
  fittings_reducing_valve: 'Not Applicable',
  fittings_additional_safety_valve: 'Not Applicable',
  fittings_other_devices: 'Not Applicable',

  safe_working_pressure_recommended_after: '6 Kg/cm²',
  repairs_required: 'NIL',
  repairs_period: 'Not Applicable',
  repairs_other_condition: 'NIL',
  reduced_working_pressure_pending_repairs: 'Not Required',
  safe_working_pressure_subrule_8: 'Not Applicable',
  other_observations: 'Safety valve operation and setting to be checked regularly.',
};

const VESSEL_TYPE_OPTIONS = [
  { value: 'Vertical Air Receiver', label: 'Vertical Air Receiver' },
  { value: 'Horizontal Air Receiver', label: 'Horizontal Air Receiver' },
  { value: 'Other', label: 'Other (Custom Entry)' },
];

const CONDITION_OPTIONS = ['Good', 'Not Good', 'Not Applicable'];

export function Form11Page() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('form11');

  const [items, setItems] = useState<Form11[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [excelImportModalOpen, setExcelImportModalOpen] = useState(false);
  const [exportIndexModalOpen, setExportIndexModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(items);

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedCompanyObj, setSelectedCompanyObj] = useState<Company | null>(null);
  const [selectedPersonnelObj, setSelectedPersonnelObj] = useState<Personnel | null>(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [personnelSigDataUrl, setPersonnelSigDataUrl] = useState<string | null>(null);
  const [personnelStampDataUrl, setPersonnelStampDataUrl] = useState<string | null>(null);

  // Pressure test calculation state
  const [pressureType, setPressureType] = useState<'WORKING' | 'DESIGN'>('WORKING');
  const [basePressureValue, setBasePressureValue] = useState<string>('6');

  // Hydraulic test helper state
  const [lastHydroDate, setLastHydroDate] = useState<string>('2023-04-11');
  const [lastHydroPressure, setLastHydroPressure] = useState<string>('9 Kg/cm²');
  const [lastHydroTester, setLastHydroTester] = useState<string>('Jagdish Patel');

  // PDF View Modal state for existing Form 11 certificate
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCertId, setViewCertId] = useState<string | null>(null);
  const [viewCertNumber, setViewCertNumber] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Persistent Global Nature of Process state (Point 5)
  const [globalNatureOfProcess, setGlobalNatureOfProcess] = useState('For storage and supply of air at 6 Kg/cm²');
  const [editingNatureOfProcess, setEditingNatureOfProcess] = useState(false);
  const [tempNatureOfProcess, setTempNatureOfProcess] = useState('');
  const [savingNatureOfProcess, setSavingNatureOfProcess] = useState(false);
  const [natureSavedToast, setNatureSavedToast] = useState(false);

  const openViewModal = async (item: Form11) => {
    setViewCertId(item.id);
    setViewCertNumber(item.certificate_number);
    setViewLoading(true);
    setViewModalOpen(true);
    try {
      const url = await api.form11.getPdfBlobUrl(item.id);
      setPdfBlobUrl(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch certificate PDF');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setViewCertId(null);
  };

  const fields = useFormFields(INITIAL_FORM11_VALUES);

  const [modalError, setModalError] = useState('');
  const [editing, setEditing] = useState<Form11 | null>(null);

  // Load persistent global settings from database on mount
  useEffect(() => {
    api.settings.get()
      .then((res) => {
        const payload = res as unknown as { data: Record<string, string> };
        const settings = payload.data ?? {};
        if (settings.form11_nature_of_process) {
          setGlobalNatureOfProcess(settings.form11_nature_of_process);
          fields.setField('nature_of_process', settings.form11_nature_of_process);
        }
      })
      .catch(() => { /* use default */ });
  }, []);

  const handleSaveNatureOfProcess = async () => {
    const newVal = tempNatureOfProcess.trim() || 'For storage and supply of air at 6 Kg/cm²';
    setSavingNatureOfProcess(true);
    try {
      await api.settings.update({ form11_nature_of_process: newVal });
      setGlobalNatureOfProcess(newVal);
      fields.setField('nature_of_process', newVal);
      setEditingNatureOfProcess(false);
      setNatureSavedToast(true);
      setTimeout(() => setNatureSavedToast(false), 3000);
    } catch {
      setError('Failed to update global Nature of Process default');
    } finally {
      setSavingNatureOfProcess(false);
    }
  };

  // Automatically calculate Hydro Test Pressure (1.5x for Working, 1.25x for Design)
  useEffect(() => {
    const val = parseFloat(basePressureValue);
    if (!isNaN(val) && val > 0) {
      const mult = pressureType === 'WORKING' ? 1.5 : 1.25;
      const hydroVal = (val * mult).toFixed(val % 1 === 0 ? 1 : 2).replace(/\.0$/, '');
      fields.setField('hydro_test_pressure', `${hydroVal} Kg/cm²`);
      fields.setField('safe_working_pressure_recommended', `${val} Kg/cm²`);
      fields.setField('safe_working_pressure_recommended_after', `${val} Kg/cm²`);
    }
  }, [basePressureValue, pressureType]);

  // External Exam Mode for Point 7a, 7c, 7d ('Date' | 'First time by us')
  const [externalExamMode, setExternalExamMode] = useState<'Date' | 'First time by us'>('Date');

  const handleExternalExamModeChange = (mode: 'Date' | 'First time by us') => {
    setExternalExamMode(mode);
    if (mode === 'First time by us') {
      fields.setField('last_external_exam_date', 'First time by us');
      fields.setField('last_hydraulic_exam_details', 'First time by us');
      fields.setField('last_ultrasonic_exam_date', 'First time by us');
    } else {
      fields.setField('last_external_exam_date', '2025-06-13');
      if (lastHydroDate) {
        const [y, m, d] = lastHydroDate.split('-');
        const formattedDate = `${d}-${m}-${y}`;
        fields.setField(
          'last_hydraulic_exam_details',
          `Last hydro test done ${lastHydroPressure || 'at 9 Kg/Cm²'} on ${formattedDate}${lastHydroTester ? ` by ${lastHydroTester}` : ''}`,
        );
      } else {
        fields.setField('last_hydraulic_exam_details', 'Last hydro test done 9 Kg/Cm2 on 11-04-2023 by Jagdish Patel');
      }
      fields.setField('last_ultrasonic_exam_date', '2025-06-13');
    }
  };

  // Internal Exam Mode for Point 7b ('Inaccessible' | 'Date')
  const [internalExamMode, setInternalExamMode] = useState<'Inaccessible' | 'Date'>('Inaccessible');

  const handleInternalExamModeChange = (mode: 'Inaccessible' | 'Date') => {
    setInternalExamMode(mode);
    if (mode === 'Inaccessible') {
      fields.setField('last_internal_exam_date', 'Inaccessible');
    } else {
      fields.setField('last_internal_exam_date', '2025-06-13');
    }
  };

  // Ultrasonic / NDT Readings (Point 9d) Shell & Dish inputs
  const [ndtShell, setNdtShell] = useState('5, 5.1, 5');
  const [ndtDish, setNdtDish] = useState('7.7, 7.65');

  useEffect(() => {
    const parts: string[] = [];
    const cleanShell = ndtShell.trim();
    if (cleanShell) {
      const sVal = /mm$/i.test(cleanShell) ? cleanShell : `${cleanShell} mm`;
      parts.push(`Shell: - ${sVal}`);
    }
    const cleanDish = ndtDish.trim();
    if (cleanDish) {
      const dVal = /mm$/i.test(cleanDish) ? cleanDish : `${cleanDish} mm`;
      parts.push(`Dish: - ${dVal}`);
    }
    fields.setField('exam_ultrasonic_findings', parts.join(', '));
  }, [ndtShell, ndtDish]);

  // Update Last Hydraulic Exam Details string automatically when hydro helper inputs change
  useEffect(() => {
    if (externalExamMode === 'First time by us') return;
    if (lastHydroDate) {
      const [y, m, d] = lastHydroDate.split('-');
      const formattedDate = `${d}-${m}-${y}`;
      fields.setField(
        'last_hydraulic_exam_details',
        `Last hydro test done ${lastHydroPressure || 'at 9 Kg/Cm²'} on ${formattedDate}${lastHydroTester ? ` by ${lastHydroTester}` : ''}`,
      );
    }
  }, [lastHydroDate, lastHydroPressure, lastHydroTester, externalExamMode]);

  // Date Mode states ('Year' | 'Date' | 'Not known')
  const [constructionMode, setConstructionMode] = useState<'Year' | 'Date' | 'Not known'>('Year');
  const [firstUseMode, setFirstUseMode] = useState<'Year' | 'Date' | 'Not known'>('Year');

  const handleConstructionModeChange = (mode: 'Year' | 'Date' | 'Not known') => {
    setConstructionMode(mode);
    if (mode === 'Not known') {
      fields.setField('date_of_construction', 'Not known');
    } else if (mode === 'Year') {
      const current = fields.values.date_of_construction || '';
      const yearMatch = current.match(/\b(19\d\d|20\d\d)\b/);
      fields.setField('date_of_construction', yearMatch ? yearMatch[1] : '1992');
    } else if (mode === 'Date') {
      const current = fields.values.date_of_construction || '';
      if (current.includes('-') && current.length >= 8) {
        fields.setField('date_of_construction', current);
      } else {
        const yearMatch = current.match(/\b(19\d\d|20\d\d)\b/);
        const y = yearMatch ? yearMatch[1] : '1992';
        fields.setField('date_of_construction', `${y}-01-01`);
      }
    }
  };

  const handleFirstUseModeChange = (mode: 'Year' | 'Date' | 'Not known') => {
    setFirstUseMode(mode);
    if (mode === 'Not known') {
      fields.setField('first_taken_into_use_date', 'Not known');
    } else if (mode === 'Year') {
      const current = fields.values.first_taken_into_use_date || '';
      const yearMatch = current.match(/\b(19\d\d|20\d\d)\b/);
      fields.setField('first_taken_into_use_date', yearMatch ? yearMatch[1] : '1992');
    } else if (mode === 'Date') {
      const current = fields.values.first_taken_into_use_date || '';
      if (current.includes('-') && current.length >= 8) {
        fields.setField('first_taken_into_use_date', current);
      } else {
        const yearMatch = current.match(/\b(19\d\d|20\d\d)\b/);
        const y = yearMatch ? yearMatch[1] : '1992';
        fields.setField('first_taken_into_use_date', `${y}-01-01`);
      }
    }
  };

  const [suggestingSerial, setSuggestingSerial] = useState(false);
  const [serialStatus, setSerialStatus] = useState<{
    loading: boolean;
    available?: boolean;
    message?: string;
    suggested?: string;
  }>({ loading: false });

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('form11').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => setDraftCount(draftManager.listDrafts('form11').length);
    update();
    const unsub = draftManager.subscribe(update);
    return unsub;
  }, []);

  const restoreForm11Draft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditing(null);
    setModalError('');
    if (data.constructionMode) setConstructionMode(data.constructionMode as any);
    if (data.firstUseMode) setFirstUseMode(data.firstUseMode as any);
    if (data.externalExamMode) setExternalExamMode(data.externalExamMode as any);
    if (data.internalExamMode) setInternalExamMode(data.internalExamMode as any);
    if (data.ndtShell) setNdtShell(String(data.ndtShell));
    if (data.ndtDish) setNdtDish(String(data.ndtDish));
    if (data.basePressureValue) setBasePressureValue(String(data.basePressureValue));
    if (data.pressureType) setPressureType(data.pressureType as any);
    if (data.fields && typeof data.fields === 'object') {
      fields.patchValues(data.fields as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restoreForm11Draft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'form11') {
        setIsResumed(true);
        restoreForm11Draft(draft);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('resumeDraft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, restoreForm11Draft]);

  const isForm11Dirty = useMemo(() => {
    if (!modalOpen || editing) return false;
    return Boolean(
      fields.values.company_id ||
      fields.values.vessel_serial_no ||
      fields.values.personnel_id ||
      fields.values.vessel_name_description !== 'Vertical Air Receiver'
    );
  }, [modalOpen, editing, fields.values]);

  const draftTitle = useMemo(() => {
    const comp = selectedCompanyObj?.company_name || 'Form 11 Inspection';
    const vessel = fields.values.vessel_name_description || 'Pressure Vessel';
    return `${comp} — ${vessel}`;
  }, [selectedCompanyObj, fields.values.vessel_name_description]);

  const draftFormData = useMemo(() => ({
    fields: fields.values,
    constructionMode,
    firstUseMode,
    externalExamMode,
    internalExamMode,
    ndtShell,
    ndtDish,
    basePressureValue,
    pressureType,
  }), [fields.values, constructionMode, firstUseMode, externalExamMode, internalExamMode, ndtShell, ndtDish, basePressureValue, pressureType]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'form11',
    draftId: 'dft_form11_new',
    enabled: modalOpen && !editing,
    isDirty: isForm11Dirty,
    isResumed,
    formData: draftFormData,
    title: draftTitle,
    subtitle: fields.values.examination_date ? `Exam Date: ${formatDateOnly(fields.values.examination_date)}` : undefined,
  });

  // Helper to compute custom vessel serial number from name words
  const getCustomVesselSerial = (customName: string) => {
    const words = customName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    const prefix = words.map((w) => w.charAt(0).toUpperCase()).join('');
    return `${prefix}/01`;
  };

  // Live Serial Number uniqueness validation
  const debouncedSerialNo = fields.values.vessel_serial_no;
  useDebounce(debouncedSerialNo, () => {
    const { company_id, vessel_serial_no, vessel_name_description, vessel_type_select } = fields.values;
    if (!company_id || !vessel_serial_no?.trim()) {
      setSerialStatus({ loading: false });
      return;
    }
    setSerialStatus({ loading: true });
    const vesselName = vessel_name_description || vessel_type_select;
    api.form11
      .checkSerialNo(company_id, vessel_serial_no.trim(), vesselName)
      .then((res) => {
        if (res.data) {
          setSerialStatus({
            loading: false,
            available: res.data.available,
            message: res.data.message,
            suggested: res.data.suggested,
          });
        }
      })
      .catch(() => setSerialStatus({ loading: false }))
      .finally(() => {
        setSerialStatus((prev) => ({ ...prev, loading: false }));
      });
  }, 350);

  // Auto-suggest incremented vessel serial number company-wise
  const handleSuggestSerialNo = async (targetCompId?: string, targetVesselName?: string) => {
    const compId = targetCompId !== undefined ? targetCompId : fields.values.company_id;
    const vesselName = targetVesselName !== undefined
      ? targetVesselName
      : (fields.values.vessel_name_description || fields.values.vessel_type_select || 'Vertical Air Receiver');

    setSuggestingSerial(true);
    try {
      const res = await api.form11.suggestSerialNo(compId, vesselName);
      if (res.data?.suggested) {
        fields.setField('vessel_serial_no', res.data.suggested);
        setSerialStatus({ loading: false, available: true });
      }
    } catch {
      const lower = vesselName.toLowerCase();
      let prefix = 'VART';
      if (lower.includes('vertical air') || lower.startsWith('vart') || lower.startsWith('var')) {
        prefix = 'VART';
      } else if (lower.includes('horizontal air') || lower.startsWith('hart') || lower.startsWith('har')) {
        prefix = 'HART';
      } else if (vesselName) {
        const words = vesselName.trim().split(/\s+/).filter(Boolean);
        const initials = words.map((w) => w.charAt(0).toUpperCase()).join('');
        prefix = initials || 'PV';
      }
      fields.setField('vessel_serial_no', `${prefix}/01`);
      setSerialStatus({ loading: false });
    } finally {
      setSuggestingSerial(false);
    }
  };

  // Handle vessel type selection change
  const handleVesselTypeChange = (typeVal: string) => {
    fields.setField('vessel_type_select', typeVal);
    if (typeVal === 'Vertical Air Receiver') {
      fields.setField('vessel_name_description', 'Vertical Air Receiver');
      handleSuggestSerialNo(fields.values.company_id, 'Vertical Air Receiver');
    } else if (typeVal === 'Horizontal Air Receiver') {
      fields.setField('vessel_name_description', 'Horizontal Air Receiver');
      handleSuggestSerialNo(fields.values.company_id, 'Horizontal Air Receiver');
    } else if (typeVal === 'Other') {
      fields.setField('vessel_name_description', '');
      fields.setField('vessel_serial_no', '');
    }
  };

  // Inline personnel creation state
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);

  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'examination_date' || field === 'next_inspection_date' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  // Fetch items list
  const loadData = async () => {
    setLoading(true);
    try {
      const res = (await api.form11.list({
        search: search || undefined,
        company_id: companyFilter || undefined,
        page,
        limit: 15,
        sortBy,
        sortOrder,
      })) as unknown as PaginatedResponse<Form11>;

      setItems((res.data as Form11[]) ?? []);
      setTotalPages(res.totalPages ?? 1);
      setTotal(res.total ?? 0);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load Form 11 list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, search, companyFilter, sortBy, sortOrder]);

  // When company selection changes, load details & prior inspection
  useEffect(() => {
    if (!fields.values.company_id) {
      setSelectedCompanyObj(null);
      return;
    }
    api.companies.get(fields.values.company_id).then((res) => {
      if (res.data) setSelectedCompanyObj(res.data as Company);
    });

    const currentVessel = fields.values.vessel_name_description || fields.values.vessel_type_select;
    if (!editing && currentVessel && fields.values.vessel_type_select !== 'Other') {
      handleSuggestSerialNo(fields.values.company_id, currentVessel);
    }
  }, [fields.values.company_id, editing]);

  useEffect(() => {
    if (editing || !fields.values.company_id) return;
    api.form11
      .getPreviousInspection(fields.values.company_id, fields.values.vessel_serial_no, fields.values.examination_date)
      .then((res) => {
        if (res.data) {
          if (res.data.last_external) fields.setField('last_external_exam_date', res.data.last_external);
          if (res.data.last_hydraulic) fields.setField('last_hydraulic_exam_details', res.data.last_hydraulic);
        }
      });
  }, [fields.values.company_id, fields.values.vessel_serial_no, editing]);

  useEffect(() => {
    if (fields.values.personnel_id) {
      api.personnel.get(fields.values.personnel_id)
        .then((res) => {
          if (res.data) setSelectedPersonnelObj(res.data as Personnel);
        })
        .catch(() => setSelectedPersonnelObj(null));

      api.personnel.getImageDataUrl(fields.values.personnel_id, 'signature')
        .then((dataUrl) => setPersonnelSigDataUrl(dataUrl))
        .catch(() => setPersonnelSigDataUrl(null));

      api.personnel.getImageDataUrl(fields.values.personnel_id, 'stamp')
        .then((dataUrl) => setPersonnelStampDataUrl(dataUrl))
        .catch(() => setPersonnelStampDataUrl(null));
    } else {
      setSelectedPersonnelObj(null);
      setPersonnelSigDataUrl(null);
      setPersonnelStampDataUrl(null);
    }
  }, [fields.values.personnel_id]);

  // Selected Personnel licence validity status
  const personnelLicenceStatus = useMemo(() => {
    return checkLicenceStatus(selectedPersonnelObj?.competency_valid_upto);
  }, [selectedPersonnelObj?.competency_valid_upto]);

  // Auto-calculated Next Inspection Date (+6 months - 1 day)
  const calculatedNextInspDate = useMemo(() => {
    if (!fields.values.examination_date) return '';
    try {
      const [y, m, d] = fields.values.examination_date.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      dt.setMonth(dt.getMonth() + 6);
      dt.setDate(dt.getDate() - 1);
      return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
    } catch {
      return '';
    }
  }, [fields.values.examination_date]);

  // Live PDF Blob URL for client-side live PDF iframe preview
  const livePdfBlobUrl = useMemo(() => {
    try {
      const vesselPrefix = (fields.values.vessel_name_description || '').toLowerCase().includes('ammonia') ? 'AMR' : 'AR';
      const certNo = `G&G/${selectedCompanyObj?.short_code || 'CO'}/${vesselPrefix}/XX`;

      let examDateDD = '';
      let hydroDueDD = '';
      if (fields.values.examination_date) {
        try {
          const [y, m, d] = fields.values.examination_date.split('-').map(Number);
          examDateDD = `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
          const hydroDt = new Date(y + 2, m - 1, d - 1);
          hydroDueDD = `${String(hydroDt.getDate()).padStart(2, '0')}-${String(hydroDt.getMonth() + 1).padStart(2, '0')}-${hydroDt.getFullYear()}`;
        } catch {
          examDateDD = fields.values.examination_date;
        }
      }

      const extFindings = fields.values.exam_external_findings || (examDateDD ? `Good on ${examDateDD}` : 'Good');
      const hydroFindings = fields.values.exam_hydraulic_findings ||
        (fields.values.hydro_test_pressure
          ? `At ${fields.values.hydro_test_pressure} by ${selectedPersonnelObj?.full_name || 'Competent Person'} on dt. ${examDateDD || 'DD-MM-YYYY'}, next hydro test due on ${hydroDueDD || 'DD-MM-YYYY'}`
          : '');

      return generateForm11ClientPDF({
        certificate_number: certNo,
        examination_date: fields.values.examination_date,
        next_inspection_date: calculatedNextInspDate,
        company: selectedCompanyObj || undefined,
        personnel: selectedPersonnelObj || undefined,
        personnel_signature_data_url: personnelSigDataUrl,
        personnel_stamp_data_url: personnelStampDataUrl,

        vessel_name_description: fields.values.vessel_name_description,
        vessel_serial_no: fields.values.vessel_serial_no,
        vessel_capacity: fields.values.vessel_capacity,
        vessel_model_no: fields.values.vessel_model_no,
        vessel_location: fields.values.vessel_location,
        vessel_description: fields.values.vessel_description,

        manufacturer_name_address: fields.values.manufacturer_name_address || '',
        nature_of_process: fields.values.nature_of_process,

        date_of_construction: fields.values.date_of_construction,
        thickness_shell: fields.values.thickness_shell,
        thickness_dish: fields.values.thickness_dish,
        thickness_top_dish: fields.values.thickness_top_dish,
        thickness_bottom_dish: fields.values.thickness_bottom_dish,
        first_taken_into_use_date: fields.values.first_taken_into_use_date,
        safe_working_pressure_recommended: fields.values.safe_working_pressure_recommended,

        last_external_exam_date: fields.values.last_external_exam_date,
        last_internal_exam_date: fields.values.last_internal_exam_date,
        last_hydraulic_exam_details: fields.values.last_hydraulic_exam_details,
        last_ultrasonic_exam_date: fields.values.last_ultrasonic_exam_date,

        lagging_removed_status: fields.values.lagging_removed_status,

        exam_external_findings: extFindings,
        exam_internal_findings: fields.values.exam_internal_findings,
        exam_hydraulic_findings: hydroFindings,
        exam_ultrasonic_findings: fields.values.exam_ultrasonic_findings,

        condition_vessel: fields.values.condition_vessel,
        condition_piping: fields.values.condition_piping,

        fittings_pressure_gauges: fields.values.fittings_pressure_gauges,
        fittings_safety_valve: fields.values.fittings_safety_valve,
        fittings_stop_valve: fields.values.fittings_stop_valve,
        fittings_reducing_valve: fields.values.fittings_reducing_valve,
        fittings_additional_safety_valve: fields.values.fittings_additional_safety_valve,
        fittings_other_devices: fields.values.fittings_other_devices,

        safe_working_pressure_recommended_after: fields.values.safe_working_pressure_recommended_after,
        repairs_required: fields.values.repairs_required,
        repairs_period: fields.values.repairs_period,
        repairs_other_condition: fields.values.repairs_other_condition,
        reduced_working_pressure_pending_repairs: fields.values.reduced_working_pressure_pending_repairs,
        safe_working_pressure_subrule_8: fields.values.safe_working_pressure_subrule_8,
        other_observations: fields.values.other_observations,
      });
    } catch {
      return null;
    }
  }, [
    fields.values,
    selectedCompanyObj,
    selectedPersonnelObj,
    personnelSigDataUrl,
    personnelStampDataUrl,
    calculatedNextInspDate,
  ]);

  const openCreateModal = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditing(null);
    setModalError('');
    fields.reset(INITIAL_FORM11_VALUES);
    if (globalNatureOfProcess) {
      fields.setField('nature_of_process', globalNatureOfProcess);
    }
    setEditingNatureOfProcess(false);
    setConstructionMode('Year');
    setFirstUseMode('Year');
    setExternalExamMode('Date');
    setInternalExamMode('Inaccessible');
    setNdtShell('5, 5.1, 5');
    setNdtDish('7.7, 7.65');
    setBasePressureValue('6');
    setPressureType('WORKING');
    setModalOpen(true);
  };

  const openEditModal = (item: Form11) => {
    setIsResumed(false);
    setEditing(item);
    setModalError('');
    setEditingNatureOfProcess(false);

    const isKnownType = item.vessel_name_description === 'Vertical Air Receiver' || item.vessel_name_description === 'Horizontal Air Receiver';

    // Set fields
    fields.patchValues({
      company_id: item.company_id ?? '',
      personnel_id: item.personnel_id ?? '',
      examination_date: item.examination_date ?? new Date().toISOString().split('T')[0],
      vessel_name_description: item.vessel_name_description ?? '',
      vessel_type_select: isKnownType ? (item.vessel_name_description ?? 'Vertical Air Receiver') : 'Other',
      vessel_serial_no: item.vessel_serial_no ?? '',
      vessel_capacity: item.vessel_capacity ?? '500 Ltrs',
      vessel_model_no: item.vessel_model_no ?? '',
      vessel_location: item.vessel_location ?? 'Utility Section',
      vessel_description: item.vessel_description ?? '',
      manufacturer_name_address: item.manufacturer_name_address ?? '',
      nature_of_process: item.nature_of_process ?? (globalNatureOfProcess || 'For storage and supply of air at 6 Kg/cm²'),
      date_of_construction: item.date_of_construction ?? '1992',
      thickness_shell: (item.thickness_shell ?? '5.5').replace(/\s*mm\s*$/i, ''),
      thickness_dish: (item.thickness_dish ?? '8').replace(/\s*mm\s*$/i, ''),
      thickness_top_dish: item.thickness_top_dish ?? '',
      thickness_bottom_dish: item.thickness_bottom_dish ?? '',
      first_taken_into_use_date: item.first_taken_into_use_date ?? '1992',
      safe_working_pressure_recommended: item.safe_working_pressure_recommended ?? '6 Kg/cm²',
      last_external_exam_date: item.last_external_exam_date ?? '2025-06-13',
      last_internal_exam_date: item.last_internal_exam_date ?? 'Inaccessible',
      last_hydraulic_exam_details: item.last_hydraulic_exam_details ?? 'Last hydro test done 9 Kg/Cm2 on 11/04/2023 by Jagdish Patel',
      last_ultrasonic_exam_date: item.last_ultrasonic_exam_date ?? '2025-06-13',
      lagging_removed_status: item.lagging_removed_status ?? 'Non-Insulated Vessel',
      exam_external_findings: item.exam_external_findings ?? '',
      exam_internal_findings: item.exam_internal_findings ?? '(b) Inaccessible',
      exam_hydraulic_findings: item.exam_hydraulic_findings ?? '',
      hydro_test_pressure: item.hydro_test_pressure ?? '9 Kg/cm²',
      exam_ultrasonic_findings: item.exam_ultrasonic_findings ?? 'Shell: - 5, 5.1, 5 mm, Dish: - 7.7, 7.65 mm',
      condition_vessel: item.condition_vessel ?? 'Good',
      condition_piping: item.condition_piping ?? 'Good',
      fittings_pressure_gauges: item.fittings_pressure_gauges ?? 'Good',
      fittings_safety_valve: item.fittings_safety_valve ?? 'Good',
      fittings_stop_valve: item.fittings_stop_valve ?? 'Good',
      fittings_reducing_valve: item.fittings_reducing_valve ?? 'Not Applicable',
      fittings_additional_safety_valve: item.fittings_additional_safety_valve ?? 'Not Applicable',
      fittings_other_devices: item.fittings_other_devices ?? 'Not Applicable',
      safe_working_pressure_recommended_after: item.safe_working_pressure_recommended_after ?? '6 Kg/cm²',
      repairs_required: item.repairs_required ?? 'NIL',
      repairs_period: item.repairs_period ?? 'Not Applicable',
      repairs_other_condition: item.repairs_other_condition ?? 'NIL',
      reduced_working_pressure_pending_repairs: item.reduced_working_pressure_pending_repairs ?? 'Not Required',
      safe_working_pressure_subrule_8: item.safe_working_pressure_subrule_8 ?? 'Not Applicable',
      other_observations: item.other_observations ?? 'Safety valve operation and setting to be checked regularly.',
    });

    // Handle Construction date mode
    const constr = item.date_of_construction ?? '';
    if (constr === 'Not known') {
      setConstructionMode('Not known');
    } else if (/^\d{4}$/.test(constr)) {
      setConstructionMode('Year');
    } else {
      setConstructionMode('Date');
    }

    // Handle First Use date mode
    const firstUse = item.first_taken_into_use_date ?? '';
    if (firstUse === 'Not known') {
      setFirstUseMode('Not known');
    } else if (/^\d{4}$/.test(firstUse)) {
      setFirstUseMode('Year');
    } else {
      setFirstUseMode('Date');
    }

    // Handle External Exam mode
    if (item.last_external_exam_date === 'First time by us') {
      setExternalExamMode('First time by us');
    } else {
      setExternalExamMode('Date');
    }

    // Handle Internal Exam mode
    if (item.last_internal_exam_date === 'Inaccessible') {
      setInternalExamMode('Inaccessible');
    } else {
      setInternalExamMode('Date');
    }

    // Parse NDT Shell & Dish readings
    const ndtStr = item.exam_ultrasonic_findings ?? '';
    const shellMatch = ndtStr.match(/Shell:\s*-\s*([^,]+)/i);
    const dishMatch = ndtStr.match(/Dish:\s*-\s*(.+)/i);
    if (shellMatch || dishMatch) {
      setNdtShell(shellMatch ? shellMatch[1].replace(/\s*mm\s*$/i, '').trim() : '');
      setNdtDish(dishMatch ? dishMatch[1].replace(/\s*mm\s*$/i, '').trim() : '');
    } else if (ndtStr) {
      setNdtShell(ndtStr.replace(/\s*mm\s*$/i, '').trim());
      setNdtDish('');
    } else {
      setNdtShell('5, 5.1, 5');
      setNdtDish('7.7, 7.65');
    }

    // Extract base pressure
    const swp = item.safe_working_pressure_recommended || '6';
    const numMatch = swp.match(/[\d.]+/);
    if (numMatch) {
      setBasePressureValue(numMatch[0]);
    }

    setModalOpen(true);
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');

    if (!fields.values.company_id) {
      setModalError('Please select a company.');
      return;
    }
    if (!fields.values.personnel_id) {
      setModalError('Please select a competent person.');
      return;
    }
    if (personnelLicenceStatus.isExpired) {
      setModalError(`Selected Competent Person (${selectedPersonnelObj?.full_name || 'Inspector'}) has an expired competency licence (expired on ${personnelLicenceStatus.formattedDate}). Cannot create or update Form 11.`);
      return;
    }
    if (!fields.values.vessel_name_description) {
      setModalError('Please enter vessel name/description.');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await api.form11.update(editing.id, fields.values);
      } else {
        await api.form11.create(fields.values);
      }
      clearDraft();
      setModalOpen(false);
      setEditing(null);
      loadData();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : (editing ? 'Failed to update certificate' : 'Failed to issue Form 11 certificate'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.form11.delete(deleteId);
      setDeleteId(null);
      setSuccess('Certificate moved to Trash.');
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete certificate');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.form11.bulkDelete(Array.from(selectedIds));
      setSuccess(`Moved ${res.data?.count ?? selectedIds.size} Form 11 certificates to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected certificates');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleDownloadPdf = async (id: string) => {
    try {
      await api.form11.downloadPdf(id);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'PDF download failed');
    }
  };

  const isForm11Valid = useMemo(() => {
    const v = fields.values;
    return (
      !!v.company_id &&
      !!v.personnel_id &&
      !!v.examination_date &&
      !!(v.vessel_name_description?.trim() || v.vessel_type_select) &&
      !!v.vessel_serial_no?.trim() &&
      !!v.safe_working_pressure_recommended?.trim()
    );
  }, [fields.values]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* ── Page Header & Filters ─────────────────── */}
      <div className="shrink-0 space-y-3 pt-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Tab Pill Toggle */}
            <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
              <button
                type="button"
                onClick={() => setActiveTab('records')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === 'records'
                    ? 'bg-white text-gray-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Form 11 Records
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('drafts')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                  activeTab === 'drafts'
                    ? 'bg-white text-amber-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Drafts</span>
                {draftCount > 0 && (
                  <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                    {draftCount}
                  </span>
                )}
              </button>
            </div>

            {activeTab === 'records' && (
              <button onClick={loadData} className="btn btn-secondary">Refresh</button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={() => setExportIndexModalOpen(true)}
              className="btn bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 font-medium px-3 text-xs flex items-center gap-1.5 rounded-lg cursor-pointer shadow-xs transition-colors"
              title="Export 6-Month Form 11 Inspection Index (PDF / Excel)"
            >
              <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Export Index</span>
            </button>
            {isWrite && (
              <>
                <button
                  onClick={() => setExcelImportModalOpen(true)}
                  className="btn bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 text-xs flex items-center gap-1.5 rounded-lg cursor-pointer shadow-xs transition-colors"
                >
                  <svg className="w-3.5 h-3.5 text-white shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Bulk Excel Import</span>
                </button>
                <button
                  onClick={openCreateModal}
                  className="btn btn-primary text-xs"
                >
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Issue Form 11</span>
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Search & Filters Bar */}
        {activeTab === 'records' && (
          <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search report no, company, vessel..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9 text-sm w-full"
              />
            </div>
            <div className="w-full sm:w-64">
              <AsyncSearchableSelect
                placeholder="Filter by Company..."
                fetchOptions={fetchCompanyOptions}
                value={companyFilter}
                onChange={(val) => setCompanyFilter(val)}
              />
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
          </div>
        )}
      </div>

      {/* ── Table & List ──────────────────────── */}
      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="form11"
            sectionTitle="Form 11"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader title="Loading Form 11 records..." />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          {items.length === 0 ? (
            <div className="py-12 text-center text-gray-500 bg-white rounded-xl border border-gray-200">
              <p className="text-base font-medium">No Form 11 certificates found.</p>
              <p className="text-xs mt-1">Click "Issue Form 11" to create your first pressure vessel report.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th className="w-10 px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        ref={masterCheckboxRef}
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                        title="Select all"
                      />
                    </th>
                    <SortableHeader label="Report No." field="certificate_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Company" field="company_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Pressure Vessel" field="vessel_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Inspector" field="inspector_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Exam Date" field="examination_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Next Inspection" field="next_inspection_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {items.map((item) => (
                    <tr key={item.id} className={isSelected(item.id) ? 'bg-brand-50/50 hover:bg-brand-50/70 transition-colors' : 'hover:bg-slate-50 transition-colors'}>
                      <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected(item.id)}
                          onChange={() => {}}
                          onClick={(e) => toggleSelect(item.id, e.shiftKey)}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                        />
                      </td>
                      <td className="font-semibold text-sm text-brand-700 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openViewModal(item)}
                          className="font-mono text-xs font-bold text-brand-600 hover:text-brand-800 hover:underline cursor-pointer text-left"
                        >
                          {item.certificate_number}
                        </button>
                      </td>
                      <td className="text-gray-900 font-medium text-sm">
                        {item.company_name ? (
                          <div className="inline-flex items-center gap-1.5">
                            <span>{item.company_name}</span>
                            {item.company_short_code && (
                              <CompanyHoverBadge shortCode={item.company_short_code} companyId={item.company_id} />
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="text-sm">
                        <div>
                          <span className="font-medium text-gray-900">{item.vessel_name_description}</span>
                          {item.vessel_serial_no && (
                            <span className="text-xs text-gray-500 block">Sr. {item.vessel_serial_no}</span>
                          )}
                        </div>
                      </td>
                      <td className="text-gray-600 text-sm">{item.inspector_name}</td>
                      <td className="whitespace-nowrap text-sm">{formatDateOnly(item.examination_date)}</td>
                      <td className="whitespace-nowrap font-medium text-emerald-700 text-sm">
                        {formatDateOnly(item.next_inspection_date)}
                      </td>
                      <td className="whitespace-nowrap">
                        <TimestampCell createdAt={item.created_at} updatedAt={item.updated_at} />
                      </td>
                      <td>
                        <div className="flex gap-1.5 items-center">
                          <button
                            onClick={() => openViewModal(item)}
                            title="View Form 11 PDF"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(item.id)}
                            title="Download Form 11 PDF"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/80 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>PDF</span>
                          </button>
                          {isWrite && (
                            <>
                              <button
                                onClick={() => openEditModal(item)}
                                title="Edit Form 11 Certificate"
                                className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setDeleteId(item.id)}
                                title="Delete Form 11"
                                className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors cursor-pointer"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsResumed(false);
          setEditing(null);
          setModalError('');
        }}
        title={editing ? `Edit Form 11 Certificate (${editing.certificate_number})` : 'Issue Form 11 Certificate (Pressure Vessel Examination)'}
        size="full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-65px)] items-stretch">
          {/* Left Panel: Form Inputs */}
          <div className="lg:col-span-5 space-y-4 overflow-y-auto h-full pr-3 pb-6">
            <DraftRecoveryBanner
              draft={existingDraft}
              onRestore={() => {
                const data = restoreDraft();
                if (data && existingDraft) {
                  restoreForm11Draft({ ...existingDraft, form_data: data });
                }
              }}
              onDiscard={discardDraft}
            />

            <form onSubmit={handleSubmit} className="space-y-4">
              {modalError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
                  <span>{modalError}</span>
                  <button type="button" onClick={() => setModalError('')} className="text-red-500 hover:text-red-700 font-bold">✕</button>
                </div>
              )}

              {/* Section 1: Basic Company & Inspector Information */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  1. Company & Inspector Selection
                </h3>
                {/* Company & Personnel Selection */}
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <label className="block text-xs font-semibold text-gray-700 whitespace-nowrap">
                        Client Company <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowCompanyForm((v) => !v)}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer whitespace-nowrap shrink-0"
                      >
                        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showCompanyForm ? 'Choose existing' : 'New Company'}
                      </button>
                    </div>
                    <AsyncSearchableSelect
                      placeholder="Search & Select Company..."
                      fetchOptions={fetchCompanyOptions}
                      value={fields.values.company_id}
                      onChange={(val) => fields.setField('company_id', val)}
                      disabled={showCompanyForm}
                    />
                    {selectedCompanyObj && !showCompanyForm && (
                      <div className="mt-2 text-xs bg-white p-2.5 rounded-lg border border-gray-200 text-gray-600 space-y-1">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-1.5">
                          <p><strong className="text-gray-800">Company:</strong> {selectedCompanyObj.company_name}</p>
                          {selectedCompanyObj.short_code && <ShortCodeTag code={selectedCompanyObj.short_code} />}
                        </div>
                        <p><strong className="text-gray-800">Occupier:</strong> {selectedCompanyObj.occupier_name}</p>
                        <p><strong className="text-gray-800">License:</strong> {selectedCompanyObj.license_number || 'N/A'}</p>
                        <p><strong className="text-gray-800">Registration:</strong> {selectedCompanyObj.registration_number || 'N/A'}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <label className="block text-xs font-semibold text-gray-700 whitespace-nowrap">
                        Competent Person <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPersonnelForm((v) => !v)}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer whitespace-nowrap shrink-0"
                      >
                        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showPersonnelForm ? 'Choose existing' : 'New Personnel'}
                      </button>
                    </div>
                    <AsyncSearchableSelect
                      placeholder="Search Competent Person..."
                      fetchOptions={fetchCompetentPersonOptions}
                      value={fields.values.personnel_id}
                      onChange={(val) => fields.setField('personnel_id', val)}
                      disabled={showPersonnelForm}
                    />
                    {selectedPersonnelObj && !showPersonnelForm && (
                      <div className="mt-2 text-xs bg-white p-2.5 rounded border border-gray-200 text-gray-600 space-y-1">
                        <p><strong className="text-gray-800">Cert No:</strong> {selectedPersonnelObj.competency_cert_number || 'N/A'}</p>
                        <p><strong className="text-gray-800">Authority:</strong> {selectedPersonnelObj.issuing_authority || 'DISH Gujarat'}</p>
                        <p>
                          <strong className="text-gray-800">Licence Valid Upto:</strong>{' '}
                          <span className={personnelLicenceStatus.isExpired ? 'text-red-700 font-bold' : personnelLicenceStatus.isExpiringSoon ? 'text-amber-700 font-bold' : 'text-gray-700'}>
                            {personnelLicenceStatus.formattedDate}
                          </span>
                        </p>
                        {personnelLicenceStatus.isExpired && (
                          <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700 flex items-start gap-1.5 shadow-2xs">
                            <span className="text-base leading-none">🔴</span>
                            <div>
                              <p className="font-bold text-red-800">Licence Expired — Form 11 Creation Blocked</p>
                              <p className="mt-0.5 text-red-700 leading-relaxed">Expired on {personnelLicenceStatus.formattedDate}. Please renew or select an active Competent Person.</p>
                            </div>
                          </div>
                        )}
                        {personnelLicenceStatus.isExpiringSoon && (
                          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-1.5 shadow-2xs">
                            <span className="text-base leading-none">⚠️</span>
                            <div>
                              <p className="font-bold text-amber-900">Licence Expiring Soon ({personnelLicenceStatus.daysRemaining} days remaining)</p>
                              <p className="mt-0.5 text-amber-800 leading-relaxed">Expires on {personnelLicenceStatus.formattedDate}.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Full Inline Company Creation Form (stretches to full section width) */}
                {showCompanyForm && (
                  <InlineCompanyForm
                    onCreated={(created) => {
                      fields.setField('company_id', created.id);
                      setSelectedCompanyObj(created as unknown as Company);
                      setShowCompanyForm(false);
                    }}
                    onCancel={() => setShowCompanyForm(false)}
                  />
                )}

                {/* Full Inline Personnel Creation Form */}
                {showPersonnelForm && (
                  <InlinePersonnelForm
                    initialRole="competent_person"
                    onCreated={(created) => {
                      fields.setField('personnel_id', created.id);
                      setShowPersonnelForm(false);
                    }}
                    onCancel={() => setShowPersonnelForm(false)}
                  />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Examination Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={fields.values.examination_date}
                      onChange={(e) => fields.setField('examination_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Next Inspection Date (Auto +6M - 1D)
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={calculatedNextInspDate}
                      className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded-lg text-sm text-emerald-700 font-semibold"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Pressure Vessel Identification (Point 3) */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  2. Pressure Vessel Identification (Point 3)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Type / Description</label>
                    <select
                      value={fields.values.vessel_type_select}
                      onChange={(e) => handleVesselTypeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      {VESSEL_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {fields.values.vessel_type_select === 'Other' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Custom Vessel Name</label>
                      <input
                        type="text"
                        value={fields.values.vessel_name_description}
                        onChange={(e) => {
                          const val = e.target.value;
                          fields.setField('vessel_name_description', val);
                          const serial = getCustomVesselSerial(val);
                          fields.setField('vessel_serial_no', serial);
                        }}
                        onBlur={() => {
                          if (fields.values.vessel_name_description?.trim()) {
                            handleSuggestSerialNo(fields.values.company_id, fields.values.vessel_name_description.trim());
                          }
                        }}
                        placeholder="e.g. Storage Tank"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Serial Number</label>
                    <div className="space-y-1">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={fields.values.vessel_serial_no}
                          onChange={(e) => {
                            const val = e.target.value;
                            fields.setField('vessel_serial_no', val);
                            if (val.trim()) {
                              setSerialStatus({ loading: true });
                            } else {
                              setSerialStatus({ loading: false });
                            }
                          }}
                          placeholder="e.g. VART/01"
                          className={`w-full px-3 py-2 border rounded-lg text-sm font-mono uppercase bg-white ${
                            !serialStatus.loading && serialStatus.available === false
                              ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => handleSuggestSerialNo()}
                          disabled={suggestingSerial}
                          title="Suggest next incremented Serial Number"
                          className="px-3 py-1.5 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 rounded-lg shrink-0 cursor-pointer flex items-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                          {suggestingSerial && (
                            <span className="animate-spin h-3 w-3 border-2 border-brand-600 border-t-transparent rounded-full" />
                          )}
                          <span>Suggest</span>
                        </button>
                      </div>
                      {serialStatus.loading && (
                        <p className="text-xs text-blue-600 font-medium flex items-center gap-1.5 pt-0.5">
                          <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                          Checking Serial Number availability…
                        </p>
                      )}
                      {!serialStatus.loading && serialStatus.available === true && fields.values.vessel_serial_no?.trim() && (
                        <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1 pt-0.5">
                          ✓ Serial Number '{fields.values.vessel_serial_no.toUpperCase()}' is available for this company
                        </p>
                      )}
                      {!serialStatus.loading && serialStatus.available === false && fields.values.vessel_serial_no?.trim() && (
                        <div className="text-xs text-red-600 font-semibold space-y-0.5 pt-0.5">
                          <p>✕ {serialStatus.message || `Serial Number '${fields.values.vessel_serial_no.toUpperCase()}' already exists for this company`}</p>
                          {serialStatus.suggested && (
                            <p>
                              Suggested Serial Number:{' '}
                              <button
                                type="button"
                                onClick={() => {
                                  fields.setField('vessel_serial_no', serialStatus.suggested!);
                                  setSerialStatus({ loading: false, available: true });
                                }}
                                className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                              >
                                Use '{serialStatus.suggested}'
                              </button>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Capacity</label>
                    <input
                      type="text"
                      value={fields.values.vessel_capacity}
                      onChange={(e) => fields.setField('vessel_capacity', e.target.value)}
                      placeholder="e.g. 3 KL"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Model No. (Optional)</label>
                    <input
                      type="text"
                      value={fields.values.vessel_model_no}
                      onChange={(e) => fields.setField('vessel_model_no', e.target.value)}
                      placeholder="e.g. HPR-01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Location in Factory</label>
                    <input
                      type="text"
                      value={fields.values.vessel_location}
                      onChange={(e) => fields.setField('vessel_location', e.target.value)}
                      placeholder="e.g. Near Compressor Room"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description / Additional Vessel Info (Optional)
                    </label>
                    <input
                      type="text"
                      value={fields.values.vessel_description}
                      onChange={(e) => fields.setField('vessel_description', e.target.value)}
                      placeholder="e.g. Additional remarks, fluid medium, operating parameters, or custom vessel details"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Manufacturer, Process & Hydro Test Calculation (Points 4, 5, 6, 9c) */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  3. Pressures & Hydro Test Multiplier Calculation
                </h3>

                {/* Multiplier toggle & base value */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-blue-900">Hydro Test Calculator Mode:</span>
                    <div className="inline-flex rounded-md shadow-sm">
                      <button
                        type="button"
                        onClick={() => setPressureType('WORKING')}
                        className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
                          pressureType === 'WORKING'
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Working Pressure (×1.5)
                      </button>
                      <button
                        type="button"
                        onClick={() => setPressureType('DESIGN')}
                        className={`px-3 py-1 text-xs font-medium rounded-r-md border ${
                          pressureType === 'DESIGN'
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Design Pressure (×1.25)
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">Base Pressure Value (Kg/cm²)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={basePressureValue}
                        onChange={(e) => setBasePressureValue(e.target.value)}
                        className="w-full px-3 py-1.5 border border-blue-300 rounded text-sm bg-white font-bold text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">Calculated Hydro Test Pressure</label>
                      <input
                        type="text"
                        readOnly
                        value={fields.values.hydro_test_pressure}
                        className="w-full px-3 py-1.5 bg-blue-100 border border-blue-300 rounded text-sm font-bold text-brand-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">Safe Working Pressure</label>
                      <input
                        type="text"
                        readOnly
                        value={fields.values.safe_working_pressure_recommended}
                        className="w-full px-3 py-1.5 bg-blue-100 border border-blue-300 rounded text-sm font-bold text-emerald-800"
                      />
                    </div>
                  </div>
                </div>

                {/* Point 4: Manufacturer Name & Address (Single Line) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">Manufacturer Name & Address (Point 4)</label>
                    <button
                      type="button"
                      onClick={() => fields.setField('manufacturer_name_address', 'Local Make')}
                      className="text-[11px] text-brand-600 hover:text-brand-800 hover:underline cursor-pointer"
                      title="Click or press Tab in empty field to fill 'Local Make'"
                    >
                      Hint: <span className="font-semibold">Local Make</span> (Tab to fill)
                    </button>
                  </div>
                  <input
                    type="text"
                    value={fields.values.manufacturer_name_address}
                    onChange={(e) => fields.setField('manufacturer_name_address', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !fields.values.manufacturer_name_address?.trim() && !e.shiftKey) {
                        fields.setField('manufacturer_name_address', 'Local Make');
                      }
                    }}
                    placeholder="Local Make"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                </div>

                {/* Point 5: Nature of Process (Fixed, Locked, Editable to new global default) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">Nature of Process (Point 5)</label>
                    {!editingNatureOfProcess && (
                      <button
                        type="button"
                        onClick={() => {
                          setTempNatureOfProcess(fields.values.nature_of_process || globalNatureOfProcess);
                          setEditingNatureOfProcess(true);
                        }}
                        className="flex items-center gap-1 text-[11px] font-medium text-brand-700 hover:text-brand-900 bg-brand-50 hover:bg-brand-100 px-2 py-0.5 rounded border border-brand-200 cursor-pointer transition-colors"
                        title="Edit global default value for all new Form 11 certificates"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span>Edit Default</span>
                      </button>
                    )}
                  </div>

                  {editingNatureOfProcess ? (
                    <div className="space-y-2 bg-amber-50/70 p-3 rounded-lg border border-amber-200">
                      <input
                        type="text"
                        value={tempNatureOfProcess}
                        onChange={(e) => setTempNatureOfProcess(e.target.value)}
                        placeholder="e.g. For storage and supply of air at 6 Kg/cm²"
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:ring-amber-500 focus:border-amber-500"
                        autoFocus
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-amber-800">
                          Saving will set this as the default for all future Form 11 certificates.
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingNatureOfProcess(false)}
                            disabled={savingNatureOfProcess}
                            className="px-2.5 py-1 text-xs text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-md cursor-pointer disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveNatureOfProcess}
                            disabled={savingNatureOfProcess || !tempNatureOfProcess.trim()}
                            className="px-3 py-1 text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 rounded-md shrink-0 cursor-pointer disabled:opacity-50 flex items-center gap-1"
                          >
                            {savingNatureOfProcess && (
                              <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                            )}
                            <span>Save as Default</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={fields.values.nature_of_process || globalNatureOfProcess}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-slate-100 text-slate-700 cursor-not-allowed select-all font-medium"
                      />
                    </div>
                  )}

                  {natureSavedToast && (
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1 pt-1 animate-fadeIn">
                      ✓ Saved as new global default for all Form 11 certificates!
                    </p>
                  )}
                </div>

                {/* Date / Year of Construction Picker (Per Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date / Year of Construction
                  </label>
                  <div className="flex gap-2 items-center">
                    <div className={`relative ${constructionMode === 'Not known' ? 'w-full sm:w-44' : 'shrink-0'}`}>
                      <select
                        value={constructionMode}
                        onChange={(e) => handleConstructionModeChange(e.target.value as 'Year' | 'Date' | 'Not known')}
                        className="w-full appearance-none pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm bg-slate-50 font-medium hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer text-gray-800 transition-colors"
                      >
                        <option value="Year">Year</option>
                        <option value="Date">Date</option>
                        <option value="Not known">Not known</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {constructionMode === 'Year' && (
                      <input
                        type="number"
                        min="1900"
                        max="2099"
                        placeholder="e.g. 1992"
                        value={fields.values.date_of_construction === 'Not known' ? '' : fields.values.date_of_construction}
                        onChange={(e) => fields.setField('date_of_construction', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white font-mono"
                      />
                    )}

                    {constructionMode === 'Date' && (
                      <input
                        type="date"
                        value={fields.values.date_of_construction.includes('-') ? fields.values.date_of_construction : ''}
                        onChange={(e) => fields.setField('date_of_construction', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    )}
                  </div>
                </div>

                {/* Date First Taken into Use Picker (Per Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date First Taken into Use
                  </label>
                  <div className="flex gap-2 items-center">
                    <div className={`relative ${firstUseMode === 'Not known' ? 'w-full sm:w-44' : 'shrink-0'}`}>
                      <select
                        value={firstUseMode}
                        onChange={(e) => handleFirstUseModeChange(e.target.value as 'Year' | 'Date' | 'Not known')}
                        className="w-full appearance-none pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm bg-slate-50 font-medium hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer text-gray-800 transition-colors"
                      >
                        <option value="Year">Year</option>
                        <option value="Date">Date</option>
                        <option value="Not known">Not known</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {firstUseMode === 'Year' && (
                      <input
                        type="number"
                        min="1900"
                        max="2099"
                        placeholder="e.g. 1992"
                        value={fields.values.first_taken_into_use_date === 'Not known' ? '' : fields.values.first_taken_into_use_date}
                        onChange={(e) => fields.setField('first_taken_into_use_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white font-mono"
                      />
                    )}

                    {firstUseMode === 'Date' && (
                      <input
                        type="date"
                        value={fields.values.first_taken_into_use_date.includes('-') ? fields.values.first_taken_into_use_date : ''}
                        onChange={(e) => fields.setField('first_taken_into_use_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Shell Thickness (mm)</label>
                    <input
                      type="text"
                      value={fields.values.thickness_shell}
                      onChange={(e) => fields.setField('thickness_shell', e.target.value)}
                      placeholder="e.g. 5.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Dish Thickness (mm)</label>
                    <input
                      type="text"
                      value={fields.values.thickness_dish}
                      onChange={(e) => fields.setField('thickness_dish', e.target.value)}
                      placeholder="e.g. 8"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Past Examination Dates & Findings (Points 7 & 9) */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  4. Past Test Dates & Findings (Points 7, 8, 9)
                </h3>

                {/* Point 7a (Single Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Last External Exam Date Picker (Point 7a)
                  </label>
                  <div className="flex gap-2 items-center">
                    <div className={`relative ${externalExamMode === 'First time by us' ? 'w-full sm:w-44' : 'shrink-0'}`}>
                      <select
                        value={externalExamMode}
                        onChange={(e) => handleExternalExamModeChange(e.target.value as 'Date' | 'First time by us')}
                        className="w-full appearance-none pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm bg-slate-50 font-medium hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer text-gray-800 transition-colors"
                      >
                        <option value="Date">Date</option>
                        <option value="First time by us">First time by us</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {externalExamMode === 'Date' && (
                      <input
                        type="date"
                        value={fields.values.last_external_exam_date.includes('-') ? fields.values.last_external_exam_date : ''}
                        onChange={(e) => fields.setField('last_external_exam_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    )}
                  </div>
                </div>

                {/* Point 7b (Single Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Last Internal Exam Date Picker (Point 7b)
                  </label>
                  <div className="flex gap-2 items-center">
                    <div className={`relative ${internalExamMode === 'Inaccessible' ? 'w-full sm:w-44' : 'shrink-0'}`}>
                      <select
                        value={internalExamMode}
                        onChange={(e) => handleInternalExamModeChange(e.target.value as 'Inaccessible' | 'Date')}
                        className="w-full appearance-none pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm bg-slate-50 font-medium hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer text-gray-800 transition-colors"
                      >
                        <option value="Inaccessible">Inaccessible</option>
                        <option value="Date">Date</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {internalExamMode === 'Date' && (
                      <input
                        type="date"
                        value={fields.values.last_internal_exam_date.includes('-') ? fields.values.last_internal_exam_date : ''}
                        onChange={(e) => fields.setField('last_internal_exam_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    )}
                  </div>
                </div>

                {/* Hydro Test Date Picker Builder / First time by us (Point 7c) (Single Line) */}
                {externalExamMode === 'First time by us' ? (
                  <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-700">Last Hydraulic Test Details (Point 7c)</span>
                    <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded font-medium border border-slate-200">First time by us</span>
                  </div>
                ) : (
                  <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-2">
                    <label className="block text-xs font-semibold text-slate-800">Last Hydraulic Test Picker & Details (Point 7c)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5">Test Date Picker</label>
                        <input
                          type="date"
                          value={lastHydroDate}
                          onChange={(e) => setLastHydroDate(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5">Pressure</label>
                        <input
                          type="text"
                          value={lastHydroPressure}
                          onChange={(e) => setLastHydroPressure(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5">Tester Name</label>
                        <input
                          type="text"
                          value={lastHydroTester}
                          onChange={(e) => setLastHydroTester(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={fields.values.last_hydraulic_exam_details}
                      onChange={(e) => fields.setField('last_hydraulic_exam_details', e.target.value)}
                      className="w-full px-3 py-1.5 bg-slate-50 border border-gray-300 rounded text-xs font-mono text-gray-700"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Last Ultrasonic Exam Date Picker (Point 7d)</label>
                    {externalExamMode === 'First time by us' ? (
                      <div className="w-full px-3 py-2 border border-slate-200 bg-slate-100 rounded-lg text-sm text-slate-700 font-medium select-none">
                        First time by us
                      </div>
                    ) : (
                      <input
                        type="date"
                        value={fields.values.last_ultrasonic_exam_date.includes('-') ? fields.values.last_ultrasonic_exam_date : ''}
                        onChange={(e) => fields.setField('last_ultrasonic_exam_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Lagging / Insulation Removal (Point 8)
                    </label>
                    <div className="relative">
                      <select
                        value={fields.values.lagging_removed_status || 'Non-Insulated Vessel'}
                        onChange={(e) => fields.setField('lagging_removed_status', e.target.value)}
                        className="w-full appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-slate-50 font-medium hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer text-gray-800 transition-colors"
                      >
                        <option value="Non-Insulated Vessel">Non-Insulated Vessel</option>
                        <option value="Insulated Vessel">Insulated Vessel</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-500">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-xs font-semibold text-slate-800">
                      Ultrasonic / NDT Readings (Point 9d)
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5 font-medium">Shell (mm)</label>
                        <input
                          type="text"
                          value={ndtShell}
                          onChange={(e) => setNdtShell(e.target.value)}
                          placeholder="e.g. 5, 5.1, 5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-600 mb-0.5 font-medium">Dish (mm)</label>
                        <input
                          type="text"
                          value={ndtDish}
                          onChange={(e) => setNdtDish(e.target.value)}
                          placeholder="e.g. 7.7, 7.65"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Conditions of Vessel, Fittings & Observations (Points 10 - 16) */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  5. Fittings Condition & Remarks (Points 10 to 16)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Condition</label>
                    <select
                      value={fields.values.condition_vessel}
                      onChange={(e) => fields.setField('condition_vessel', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Piping Condition</label>
                    <select
                      value={fields.values.condition_piping}
                      onChange={(e) => fields.setField('condition_piping', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pressure Gauges</label>
                    <select
                      value={fields.values.fittings_pressure_gauges}
                      onChange={(e) => fields.setField('fittings_pressure_gauges', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Safety Valve</label>
                    <select
                      value={fields.values.fittings_safety_valve}
                      onChange={(e) => fields.setField('fittings_safety_valve', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Stop Valve</label>
                    <select
                      value={fields.values.fittings_stop_valve}
                      onChange={(e) => fields.setField('fittings_stop_valve', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Reducing Valve</label>
                    <select
                      value={fields.values.fittings_reducing_valve}
                      onChange={(e) => fields.setField('fittings_reducing_valve', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Repairs Required (Point 13a)
                    </label>
                    <input
                      type="text"
                      value={fields.values.repairs_required}
                      onChange={(e) => fields.setField('repairs_required', e.target.value)}
                      placeholder="e.g. NIL"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Period for Repairs (Point 13b)
                    </label>
                    <input
                      type="text"
                      value={fields.values.repairs_period}
                      onChange={(e) => fields.setField('repairs_period', e.target.value)}
                      placeholder="e.g. Not Applicable"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Other Conditions (Point 13c)
                    </label>
                    <input
                      type="text"
                      value={fields.values.repairs_other_condition}
                      onChange={(e) => fields.setField('repairs_other_condition', e.target.value)}
                      placeholder="e.g. NIL"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>
                </div>

                {/* Point 14 (Single Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Reduced Working Pressure Pending Repairs (Point 14)
                  </label>
                  <input
                    type="text"
                    value={fields.values.reduced_working_pressure_pending_repairs}
                    onChange={(e) => fields.setField('reduced_working_pressure_pending_repairs', e.target.value)}
                    placeholder="Default: Not Required"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                </div>

                {/* Point 15 (Single Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Safe Working Pressure - Sub-rule 8 (Point 15)
                  </label>
                  <input
                    type="text"
                    value={fields.values.safe_working_pressure_subrule_8}
                    onChange={(e) => fields.setField('safe_working_pressure_subrule_8', e.target.value)}
                    placeholder="Default: Not Applicable"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                </div>

                {/* Point 16 (Single Line) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Other Observations (Point 16)
                  </label>
                  <input
                    type="text"
                    value={fields.values.other_observations}
                    onChange={(e) => fields.setField('other_observations', e.target.value)}
                    placeholder="Safety valve operation and setting to be checked regularly."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                </div>
              </div>

              {modalError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
                  <span>{modalError}</span>
                  <button type="button" onClick={() => setModalError('')} className="text-red-500 hover:text-red-700 font-bold">✕</button>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white/95 backdrop-blur py-2">
                <button
                  type="button"
                  onClick={() => { setModalOpen(false); setIsResumed(false); }}
                  className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !isForm11Valid}
                  className="btn btn-primary text-sm h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white shrink-0" />}
                  {saving ? (editing ? 'Saving Changes...' : 'Generating PDF...') : (editing ? 'Save Changes' : 'Issue Form 11 Certificate')}
                </button>
              </div>
            </form>
          </div>

          {/* Right Panel: Live Vector PDF Document Preview */}
          <div className="lg:col-span-7 bg-slate-100 p-4 rounded-xl border border-slate-300 flex flex-col h-full shadow-inner">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                Live Form 11 PDF Document Preview
              </span>
              <span className="text-xs text-slate-500">Official Rule 61 Format</span>
            </div>

            {livePdfBlobUrl ? (
              <iframe
                src={livePdfBlobUrl}
                className="w-full h-full min-h-[580px] rounded-lg border border-slate-300 shadow bg-white"
                title="Live Form 11 PDF Preview"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                Generating PDF document preview...
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Direct PDF Preview Modal for existing Form 11 Certificate ── */}
      <Modal
        open={viewModalOpen}
        onClose={closeViewModal}
        title={`Form 11 PDF Document — ${viewCertNumber}`}
        size="xl"
      >
        {viewLoading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-3">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-brand-600" />
            <p className="text-sm font-medium text-gray-600">Loading Form 11 PDF Document...</p>
          </div>
        ) : pdfBlobUrl ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border border-slate-200">
              <span className="text-xs font-semibold text-gray-700">{viewCertNumber}</span>
              {viewCertId && (
                <button
                  onClick={() => handleDownloadPdf(viewCertId)}
                  className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download PDF
                </button>
              )}
            </div>

            <iframe
              src={pdfBlobUrl}
              className="w-full h-[76vh] rounded-lg border border-slate-300 shadow-inner bg-slate-100"
              title={`Form 11 PDF ${viewCertNumber}`}
            />
          </div>
        ) : (
          <div className="py-12 text-center text-red-600 text-sm">
            Failed to load PDF document.
          </div>
        )}
      </Modal>

      {/* ── Bulk Excel Import Modal ──────────────── */}
      <Form11ExcelImportModal
        open={excelImportModalOpen}
        onClose={() => setExcelImportModalOpen(false)}
        onSuccess={() => {
          setExcelImportModalOpen(false);
          loadData();
        }}
      />

      {/* ── Export Index Modal ───────────────────── */}
      <Form11ExportIndexModal
        open={exportIndexModalOpen}
        onClose={() => setExportIndexModalOpen(false)}
        initialCompanyId={companyFilter}
      />

      {/* ── Confirm Delete Dialog ───────────────── */}
      <ConfirmDialog
        open={!!deleteId}
        title="Move Certificate to Trash"
        message="Are you sure you want to delete this pressure vessel examination certificate? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="certificates"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Certificates to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected Form 11 certificates? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}
